import React, { useContext, useMemo, useState, lazy, Suspense } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import {
  Card,
  CardContent,
  useMediaQuery,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';

import PrintIcon from '@mui/icons-material/Print';
import DoneIcon from '@mui/icons-material/Done';

import Fab from '@mui/material/Fab';
import ListItemText from '@mui/material/ListItemText';
import { Link as RouterLink } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import { SystemFieldType } from '@/api';
import { MeetupContext } from '@/MeetupContext';
import { AppContext } from '@/AppContext';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

const PREFIX = 'ReceptionTable';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  inner: `${PREFIX}-inner`,
  nameContainer: `${PREFIX}-nameContainer`,
  avatar: `${PREFIX}-avatar`,
  actions: `${PREFIX}-actions`,
  fab: `${PREFIX}-fab`,
  modal: `${PREFIX}-modal`,
  paper: `${PREFIX}-paper`,
  photo: `${PREFIX}-photo`,
  table: `${PREFIX}-table`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflowX: 'auto',
  },

  [`& .${classes.content}`]: {
    padding: 0,
  },

  [`& .${classes.inner}`]: {
    minWidth: 600,
  },

  [`& .${classes.nameContainer}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.avatar}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    justifyContent: 'flex-end',
  },

  [`& .${classes.fab}`]: {
    margin: theme.spacing(1),
    height: '36px !important',
    width: '36px !important',
    '& svg': {
      height: '24px !important',
      width: '24px !important',
    },
  },

  [`& .${classes.modal}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.paper}`]: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },

  [`& .${classes.photo}`]: {
    maxWidth: '70px',
    maxHeight: '70px',
    borderRadius: '7%',
  },

  [`& .${classes.table}`]: {
    overflow: 'scroll',
  },
}));

const BadgeCardForVisitor = lazy(
  () => import('../../../BadgeDetails/components/BadgeViewWithDownload/BadgeViewForVisitor')
);

const makeFIO = visitor => {
  if (visitor.middleName) {
    return [visitor.lastName, visitor.firstName, visitor.middleName || ''].join(' ');
  } else {
    return [visitor.firstName, visitor.lastName].join(' ');
  }
};

const ReceptionTable = props => {
  const { className, meetupId, visitors, printFunction, checkinFunction, topRefresh, checkinMode, ...rest } = props;

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const appContext = useContext(AppContext);
  const meetupContext = useContext(MeetupContext);

  const groupsColor = {};
  for (let group of meetupContext.groups) {
    groupsColor[group.groupId] = group.color || '#FFF';
  }

  const [modal, setModal] = useState(undefined);

  const optionalFields = useMemo(() => {
    const saved = meetupContext.tableFields;
    const EXCLUDED = new Set([
      SystemFieldType.FirstName,
      SystemFieldType.LastName,
      SystemFieldType.MiddleName,
      SystemFieldType.Group,
      SystemFieldType.QrToken,
    ]);
    const fields = meetupContext.fields.filter(f => !EXCLUDED.has(f.systemField));
    if (!Array.isArray(saved) || saved.length === 0) return fields;
    const visibleIds = saved.filter(c => c.visible).map(c => c.id);
    const orderMap = Object.fromEntries(visibleIds.map((id, i) => [id, i]));
    return fields
      .filter(f => orderMap[f.systemField || f.name] !== undefined)
      .sort((a, b) => orderMap[a.systemField || a.name] - orderMap[b.systemField || b.name]);
  }, [meetupContext.fields, meetupContext.tableFields]);

  const handleClose = () => {
    setModal(undefined);
  };
  const showModal = visitorId => {
    setModal(visitorId);
  };

  return (
    <StyledCard {...rest} className={clsx(classes.root, className)}>
      <CardContent className={classes.content}>
        <div className={classes.inner}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ФИО</TableCell>
                <TableCell>Группа</TableCell>
                {isLargeScreen && (
                  <>
                    {optionalFields.map(f => (
                      <TableCell key={f.systemField || f.name}>{f.label}</TableCell>
                    ))}
                  </>
                )}
                <TableCell>Статус</TableCell>
                {isLargeScreen && <TableCell></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {visitors.slice(0, 50).map(visitor => (
                <TableRow
                  className={classes.tableRow}
                  hover
                  key={visitor.visitorId}
                  style={{ backgroundColor: groupsColor[visitor.groupId] }}
                >
                  <TableCell>
                    <RouterLink to={'/reception/visitor/id/' + visitor.visitorId}>
                      <ListItemText primary={makeFIO(visitor)} />
                    </RouterLink>
                  </TableCell>
                  <TableCell>
                    <ListItemText primary={visitor.groupName} />
                  </TableCell>
                  {isLargeScreen && (
                    <>
                      {optionalFields.map(f => (
                        <TableCell key={f.systemField || f.name}>
                          {f.systemField === SystemFieldType.Photo ? (
                            visitor.photo && <img className={classes.photo} src={'/api/image/' + visitor.photo} />
                          ) : (
                            <ListItemText
                              primary={f.systemField ? visitor[f.systemField] : (visitor.extended || {})[f.name]}
                            />
                          )}
                        </TableCell>
                      ))}
                    </>
                  )}

                  <TableCell>
                    {visitor.status && (
                      <ListItemText
                        primary={visitor.status.status === 'come' ? 'Уже отмечен' : ''}
                        secondary={visitor.status.badgeStatus === 'printed' ? 'Бейдж напечатан' : ''}
                      />
                    )}
                  </TableCell>
                  {isLargeScreen && (
                    <TableCell style={{ textAlign: 'center', width: '150px' }}>
                      {checkinMode ? (
                        <Fab
                          size='small'
                          color={visitor.status.status === 'come' ? '' : 'primary'}
                          aria-label='checkin'
                          className={classes.fab}
                          onClick={() => checkinFunction(visitor)}
                        >
                          <DoneIcon style={visitor.status.status === 'come' ? { fill: 'green' } : {}} />
                        </Fab>
                      ) : (
                        <Fab
                          size='small'
                          color='secondary'
                          aria-label='print'
                          className={classes.fab}
                          onClick={() => printFunction(visitor)}
                        >
                          <PrintIcon />
                        </Fab>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      <Modal
        className={classes.modal}
        aria-labelledby='simple-modal-title'
        aria-describedby='simple-modal-description'
        open={!!modal}
        onClose={handleClose}
      >
        <Suspense fallback={<LoadingSpinner />}>
          <div className={classes.paper}>
            <BadgeCardForVisitor meetupId={meetupId} visitorId={modal} />
          </div>
        </Suspense>
      </Modal>
    </StyledCard>
  );
};

ReceptionTable.propTypes = {
  className: PropTypes.string,
  visitors: PropTypes.array.isRequired,
  handleDelete: PropTypes.func,
};

export default ReceptionTable;
